import { CODE, ROW_TYPE } from 'Dictionaries/docForm/docFormEnums';
import { DocFormValidationError } from 'js/vue/errors/DocFormValidationError';
import { CustomFieldValidationError } from 'js/vue/errors/CustomFieldValidationError';
import { fromSnakeToCamel } from 'Helpers/caseConversion';

export const formatDocumentData = (metadata) => {
    const document = metadata.document ?? metadata.parent;
    const paymentMethodsIds = (metadata.payment_methods ?? []).map(({ id }) => id);

    const isBankAccountValid = metadata.bank_accounts.some(
        (bankAccount) => bankAccount.id === document.bank_account_id,
    );

    let documentDataFormatted = {
        date: metadata.date ?? moment().format('YYYY-MM-DD'),
        companyReference: document.company_reference ?? '',
        number: metadata.document?.number ?? metadata.next_invoice_number,
        orderReference: metadata.document?.order_reference ?? '',
        subject: document.subject ?? metadata.subject,
        note: document.note ?? metadata.note,
        rateCategoryId: document.rate_category_id ?? metadata.rate_category_id,
        layoutId: metadata.layout_id,
        langId: metadata.lang_id ?? 0,
        isTaxesInc: document.is_taxes_inc,
        vatMode: document.vat_mode,
        vatMention: document.vat_mention,
        paymentMethods: (document.payment_method_ids ?? []).filter((paymentMethodId) =>
            paymentMethodsIds.includes(paymentMethodId),
        ),
        paymentTerms: formatPaymentTerms(document, metadata.payment_terms),
        settings: {
            payments: {
                paymentModules: document._embed?.settings.payments.payment_modules,
                directDebitModule: document._embed?.settings.payments.direct_debit_module ?? 'none',
            },
            pdfDisplay: Object.entries(document._embed?.settings.pdf_display ?? {}).reduce(
                (acc, [key, value]) => {
                    acc[fromSnakeToCamel(key)] = value;
                    return acc;
                },
                {},
            ),
            shipping: {
                parcelCount: document._embed?.settings.shipping.parcel_count,
                trackingNumber: document._embed?.settings.shipping.tracking_number,
                trackingUrl: document._embed?.settings.shipping.tracking_url,
                shippingWeight: document.shipping_weight,
                shippingVolume: document.shipping_volume,
                shippingDate: document.shipping_date,
            },
            assignedStaffLabelId: document._embed?.settings.assigned_staff_label_id,
        },
        _embed: {
            paymentTerms: document._embed?.payment_terms ?? {},
        },
        bankAccountId: isBankAccountValid
            ? document.bank_account_id
            : (metadata.bank_accounts.at(0)?.id ?? null),
        checkLabelId: document.check_label_id,
        related: document.related,
        analyticCode: document.analytic_code ?? '',
        deliveryAddressId: document.delivery_address_id,
    };

    if (metadata.parent) {
        documentDataFormatted = {
            ...documentDataFormatted,
            parent: {
                type: metadata.parent.doctype,
                id: metadata.parent.id,
                name: metadata.parent.number,
                date: metadata.parent.date,
            },
        };
    }
    if (metadata.document) {
        documentDataFormatted = {
            ...documentDataFormatted,
            docId: metadata.document.id,
        };
    }
    return documentDataFormatted;
};

export const formatItemRow = (row) => {
    let rowFormatted = {
        type: row.type,
        id: row.id,
        image: row.image,
        reference: row.reference,
        description: row.description ?? '',
        unitAmount: row.unit_amount,
        quantity: row.quantity,
        taxLabel: row.tax_label,
        taxRate: row.tax_rate,
        amountTaxExc: row.amount_tax_exc,
        amountTaxInc: row.amount_tax_inc,
        accountingCode: row.accounting_code_id,
        hasSubrow: true,
        discount: row.discount,
        unitId: row.unit_id,
        purchaseAmount: row.purchase_amount ?? 0,
        analyticCode: row.analytic_code ?? '',
        related: row.related,
    };

    if (isCatalogRow(row)) {
        rowFormatted = {
            ...rowFormatted,
            skuId: row.sku_id,
            skuCode: row.sku_code,
        };
    }

    return rowFormatted;
};

export const isProductRow = (row) => {
    return [
        ROW_TYPE.SINGLE,
        ROW_TYPE.SHIPPING,
        ROW_TYPE.PACKAGING,
        ROW_TYPE.CATALOG,
        ROW_TYPE.PROGRESS_SINGLE,
        ROW_TYPE.PROGRESS_SHIPPING,
        ROW_TYPE.PROGRESS_PACKAGING,
        ROW_TYPE.PROGRESS_CATALOG,
    ].includes(row.type);
};

export const isTextRow = (row) => {
    return [ROW_TYPE.COMMENT, ROW_TYPE.TITLE].includes(row.type);
};

export const isCatalogRow = (row) => {
    return [ROW_TYPE.CATALOG, ROW_TYPE.PROGRESS_CATALOG].includes(row.type);
};

const formatPaymentTerms = (document, paymentTerms) => {
    if (document.payment_terms) {
        return document.payment_terms;
    }

    const selectedPaymentTerm =
        paymentTerms.find(
            (paymentTerm) => paymentTerm.id === document._embed?.payment_terms.settings.value.id,
        ) ?? paymentTerms[0];

    const requiredFields = {};

    switch (selectedPaymentTerm.code) {
        case CODE.THIRTYDAYS:
        case CODE.FORTYFIVEDAYS:
        case CODE.SIXTYDAYS:
            requiredFields.end_of_month =
                document._embed?.payment_terms.settings.value.end_of_month;
            break;
        case CODE.XDAYS:
            requiredFields.x_days = document._embed?.payment_terms.settings.value.x_days;
            break;
        case CODE.CUSTOM:
            requiredFields.due_date =
                document._embed?.payment_terms.computed_instalment_plan[0].date;
            break;
        case CODE.SCALED: {
            const today = moment();
            const computedDate = moment(
                document._embed?.payment_terms.computed_instalment_plan[0].date,
            );

            requiredFields.x_days = computedDate.diff(today, 'days') + 1;
            requiredFields.description =
                document._embed?.payment_terms.computed_instalment_plan[0].description;
            break;
        }
        case CODE.DEADLINES:
            requiredFields.deadlines = document._embed?.payment_terms.computed_instalment_plan;
            break;
        case CODE.ONORDER:
        case CODE.ENDMONTH:
        case CODE.NEXTMONTH:
        case CODE.ONINVOICE:
        case CODE.NONE:
        case CODE.RECEIVED:
        default:
            break;
    }

    return {
        id: selectedPaymentTerm.id,
        code: selectedPaymentTerm.code,
        ...requiredFields,
    };
};

export const calculatePaymentsToLink = (payments, totalDueAmount) => {
    const paymentsToLink = [];

    for (const payment of payments) {
        totalDueAmount = Number(totalDueAmount) || 0;
        if (totalDueAmount <= 0) {
            totalDueAmount = totalDueAmount.toFixed(2);
            break;
        }

        const paymentAmount = Number(payment.amount.value || 0);
        const amountToLink = Math.min(paymentAmount, totalDueAmount);
        paymentsToLink.push({
            ...payment,
            amount: amountToLink,
        });

        if (payment.type === 'credit') {
            totalDueAmount -= amountToLink;
        } else {
            totalDueAmount += amountToLink;
        }
        totalDueAmount = totalDueAmount.toFixed(2);
    }

    return { paymentsToLink, totalDueAmount };
};

export const logError = (error) => {
    if (
        error instanceof DocFormValidationError ||
        error instanceof CustomFieldValidationError ||
        error.name === 'AbortError'
    ) {
        return;
    }
    window.sellsyapp.config.globalProperties.$store.state.system.errorsHandler.logException(error);
};

export const formatRecipient = (data) => {
    const document = data.document ?? data.parent;
    return {
        contact: document._embed.contact,
        address: document._embed.invoicing_address,
        name: document.company_name,
    };
};

export const formatSender = (data, documentData) => {
    const document = data.document ?? data.parent;
    return {
        ...data.issuer,
        address: document._embed.issuer_address,
        email: document._embed.assigned_staff?.email ?? null,
        lastName: document._embed.assigned_staff?.lastname ?? null,
        firstName: document._embed.assigned_staff?.firstname ?? null,
        id: document._embed.assigned_staff?.id ?? null,
        labelAssignedStaff:
            data.assigned_staff_labels.find(
                (label) => label.id === documentData.settings.assignedStaffLabelId,
            ) ?? data.assigned_staff_labels[0],
    };
};

export const formatMetadata = (data) => {
    return {
        settings: {
            rateCategories: data.rate_categories,
            vatModes: data.vat_modes,
            docLayouts: data.document_layouts,
            languages: data.languages,
            paymentMethods: data.payment_methods,
            paymentTerms: data.payment_terms,
            paymentModules: data.payment_modules,
            directDebitModules: data.direct_debit_modules,
            weightUnits: data.weight_units,
            bankAccounts: data.bank_accounts,
            checkLabels: data.check_labels,
        },
        permissions: data.permissions,
        analytics: data.analytics,
        assignedStaffLabels: data.assigned_staff_labels,
        accountingCodesList: data.accounting_codes?.sell ?? [],
        shippingAccountingCodesList: data.accounting_codes?.shipping ?? [],
        numberFormat: data.number_format,
        lastValidationDate: data.last_validation_date,
        civilities: data.civilities,
    };
};

export const formatLawTexts = (data) => {
    return {
        tvaLawText: data.law_texts.tva_law_text,
        penaltyLateWarningText: data.law_texts.penalty_retard_warning_text,
        tvaLawTextChecked: data.law_texts.has_tva_law_text,
        penaltyLateWarningTextChecked: data.law_texts.has_penalty_retard_warning_text,
    };
};

export const formatGenericStoreData = (data, overrideParameters) => {
    const documentData = formatDocumentData(data);
    return {
        documentData,
        rows: [],
        createDocument: null,
        updateDocument: null,
        config: null,
        taxes: data.document?.taxes ?? [],
        recipient: formatRecipient(data),
        sender: formatSender(data, documentData),
        metadata: formatMetadata(data),
        lawTexts: formatLawTexts(data),
        ...overrideParameters,
    };
};
